import type { CSSProperties } from "react";
import { productPageBackgroundCssVars } from "../../SiteConfig";

/** 圆角类控件样式 */
export interface IntelBorderRadiusStyle {
  /** 圆角（px） */
  borderRadius: number;
}

/** 带背景色的按钮样式 */
export interface IntelButtonStyle extends IntelBorderRadiusStyle {
  /** 背景色 */
  backgroundColor: string;
}

/** 搜索按钮样式（含字重、hover） */
export interface IntelSearchButtonStyle extends IntelButtonStyle {
  /** 字重 */
  fontWeight: number;
  /** hover 背景色 */
  hoverBackgroundColor: string;
}

/** 右侧关联说明区小标题样式（风险提示、关联个股） */
export interface IntelDetailSectionTitleStyle {
  /** 字号（px） */
  fontSize: number;
  /** 字重 */
  fontWeight: number;
  /** 文字颜色 */
  color: string;
}

/** 关联个股卡片标题行行情（价格 + 涨跌幅）样式 */
export interface IntelDetailStockQuoteStyle {
  /** 字号（px） */
  fontSize: number;
  /** 金融字体族 */
  fontFamily: string;
  /** 字重 */
  fontWeight: number;
}

/** 关联个股卡片样式 */
export interface IntelDetailStockCardStyle extends IntelBorderRadiusStyle {
  /** 默认背景色 */
  backgroundColor: string;
  /** hover 背景色 */
  hoverBackgroundColor: string;
  /** 卡片顶部内边距（px）：标题行距卡片顶边的空白 */
  paddingTop: number;
  /** 卡片左右内边距（px） */
  paddingX: number;
  /** 卡片底部内边距（px） */
  paddingBottom: number;
  /** 标题行（股票名 + 行情）上内边距（px） */
  headPaddingTop: number;
  /** 标题行（股票名 + 行情）下内边距（px） */
  headPaddingBottom: number;
  /** 标题行与推荐正文之间的间距（px） */
  headToContentGap: number;
  /** 标题行右侧行情样式 */
  quote: IntelDetailStockQuoteStyle;
}

/** 关联个股卡片基本面分 / 位置分样式 */
export interface IntelDetailStockAssessScoreStyle {
  /** 图标字号（px） */
  iconFontSize: number;
  /** 分值文案字号（px） */
  valueFontSize: number;
  /** 分值文案金融字体族 */
  valueFontFamily: string;
  /**
   * 分值相对图标的垂直光学偏移（px）。
   * 等宽数字字体基线常偏上，默认略下移与 antd 图标中线对齐。
   */
  valueAlignOffsetY: number;
}

/** 右侧关联说明区样式配置结构 */
export interface IntelDetailPanelStyle {
  /** 常驻免责提示文字颜色 */
  disclaimerColor: string;
  sectionTitle: IntelDetailSectionTitleStyle;
  stockCard: IntelDetailStockCardStyle;
  stockAssessScore: IntelDetailStockAssessScoreStyle;
}

/** 情报列表卡片边框样式 */
export interface IntelFeedCardStyle {
  /** 可点击卡片 hover 时外框颜色 */
  hoverBorderColor: string;
  /** 选中卡片外框颜色 */
  selectedBorderColor: string;
}

/** 情报页筛选区控件样式配置结构 */
export interface IntelPageFilterStyle {
  /** 筛选行控件统一高度（px） */
  controlHeight: number;
  keywordInput: IntelBorderRadiusStyle;
  searchButton: IntelSearchButtonStyle;
  viewLatestButton: IntelButtonStyle;
}

/** 情报列表卡片边框样式配置 */
export const INTEL_FEED_CARD_STYLE = {
  hoverBorderColor: "rgb(255, 216, 243)",
  selectedBorderColor: " #fc9ce5",
} as const satisfies IntelFeedCardStyle;

/** 情报页筛选区控件样式配置 */
export const INTEL_PAGE_FILTER_STYLE = {
  /** 筛选行控件统一高度（px）：关键词输入框、搜索、查看最新 */
  controlHeight: 32,
  /** 关键词输入框圆角 */
  keywordInput: {
    borderRadius: 5,
  },
  /** 搜索按钮圆角、背景色、字重与 hover 背景色 */
  searchButton: {
    backgroundColor: " #ff85c0",
    borderRadius: 5,
    fontWeight: 400,
    hoverBackgroundColor: "#ff66b3",
  },
  /** 查看最新按钮圆角与背景色 */
  viewLatestButton: {
    backgroundColor: " #ffffff",
    borderRadius: 5,
  },
} as const satisfies IntelPageFilterStyle;

/** 右侧关联说明区样式配置 */
export const INTEL_DETAIL_PANEL_STYLE = {
  /** 常驻免责提示「相关标的由模型…」文字颜色 */
  disclaimerColor: "rgb(87, 87, 87)",
  /** 「风险提示」「关联个股」小标题 */
  sectionTitle: {
    fontSize: 12,
    fontWeight: 400,
    color: "rgba(22, 119, 255, 0.85)",
  },
  /** 关联个股卡片：无边框，浅色底，hover 加深 */
  stockCard: {
    borderRadius: 8,
    backgroundColor: "rgb(253, 238, 254)",
    hoverBackgroundColor: "rgb(254, 227, 253)",
    paddingTop: 4,
    paddingX: 12,
    paddingBottom: 12,
    headPaddingTop: 0,
    headPaddingBottom: 0,
    headToContentGap: 0,
    quote: {
      fontSize: 13,
      fontFamily: '"JetBrains Mono", "SFMono-Regular", ui-monospace, "Liberation Mono", Menlo, Consolas, monospace',
      fontWeight: 400,
    },
  },
  /** 卡片左下角基本面分 / 位置分 */
  stockAssessScore: {
    iconFontSize: 13,
    valueFontSize: 13,
    valueFontFamily:
      '"JetBrains Mono", "SFMono-Regular", ui-monospace, "Liberation Mono", Menlo, Consolas, monospace',
    valueAlignOffsetY: 0.5,
  },
} as const satisfies IntelDetailPanelStyle;

/** 新闻类型筛选项（与 LLM / intelligence.news_type 一致） */
export const INTEL_NEWS_TYPE_OPTIONS = [
  { type: 0, label: "综合新闻" },
  { type: 1, label: "政策法规" },
  { type: 2, label: "前沿科技" },
  { type: 3, label: "突发事件" },
  { type: 4, label: "业绩盈亏" },
  { type: 5, label: "风险警示" },
  { type: 6, label: "行业动向" },
  { type: 7, label: "资金动向" },
  { type: 8, label: "大宗市场" },
  { type: 9, label: "宏观经济" },
  { type: 10, label: "全球贸易" },
] as const;

/** 供页面根节点注入 CSS 变量，驱动筛选区输入框与按钮样式 */
export function intelPageCssVars(): CSSProperties {
  const { hoverBorderColor, selectedBorderColor } = INTEL_FEED_CARD_STYLE;
  const { controlHeight, keywordInput, searchButton, viewLatestButton } = INTEL_PAGE_FILTER_STYLE;
  const { disclaimerColor, sectionTitle, stockCard, stockAssessScore } = INTEL_DETAIL_PANEL_STYLE;
  return {
    ...productPageBackgroundCssVars(),
    ["--lf-intel-card-hover-border-color" as string]: hoverBorderColor,
    ["--lf-intel-card-selected-border-color" as string]: selectedBorderColor,
    ["--lf-intel-filter-control-height" as string]: `${controlHeight}px`,
    ["--lf-intel-kw-radius" as string]: `${keywordInput.borderRadius}px`,
    ["--lf-intel-search-btn-bg" as string]: searchButton.backgroundColor,
    ["--lf-intel-search-btn-hover-bg" as string]: searchButton.hoverBackgroundColor,
    ["--lf-intel-search-btn-radius" as string]: `${searchButton.borderRadius}px`,
    ["--lf-intel-search-btn-font-weight" as string]: String(searchButton.fontWeight),
    ["--lf-intel-view-latest-btn-bg" as string]: viewLatestButton.backgroundColor,
    ["--lf-intel-view-latest-btn-radius" as string]: `${viewLatestButton.borderRadius}px`,
    ["--lf-intel-detail-disclaimer-color" as string]: disclaimerColor,
    ["--lf-intel-detail-section-title-font-size" as string]: `${sectionTitle.fontSize}px`,
    ["--lf-intel-detail-section-title-font-weight" as string]: String(sectionTitle.fontWeight),
    ["--lf-intel-detail-section-title-color" as string]: sectionTitle.color,
    ["--lf-intel-stock-card-radius" as string]: `${stockCard.borderRadius}px`,
    ["--lf-intel-stock-card-bg" as string]: stockCard.backgroundColor,
    ["--lf-intel-stock-card-hover-bg" as string]: stockCard.hoverBackgroundColor,
    ["--lf-intel-stock-card-padding-top" as string]: `${stockCard.paddingTop}px`,
    ["--lf-intel-stock-card-padding-x" as string]: `${stockCard.paddingX}px`,
    ["--lf-intel-stock-card-padding-bottom" as string]: `${stockCard.paddingBottom}px`,
    ["--lf-intel-stock-head-padding-top" as string]: `${stockCard.headPaddingTop}px`,
    ["--lf-intel-stock-head-padding-bottom" as string]: `${stockCard.headPaddingBottom}px`,
    ["--lf-intel-stock-head-to-content-gap" as string]: `${stockCard.headToContentGap}px`,
    ["--lf-intel-stock-quote-font-size" as string]: `${stockCard.quote.fontSize}px`,
    ["--lf-intel-stock-quote-font" as string]: stockCard.quote.fontFamily,
    ["--lf-intel-stock-quote-font-weight" as string]: String(stockCard.quote.fontWeight),
    ["--lf-intel-stock-score-icon-size" as string]: `${stockAssessScore.iconFontSize}px`,
    ["--lf-intel-stock-score-value-size" as string]: `${stockAssessScore.valueFontSize}px`,
    ["--lf-intel-stock-score-value-font" as string]: stockAssessScore.valueFontFamily,
    ["--lf-intel-stock-score-value-offset-y" as string]: `${stockAssessScore.valueAlignOffsetY}px`,
    ["--lf-intel-stock-score-row-size" as string]: `${Math.max(
      stockAssessScore.iconFontSize,
      stockAssessScore.valueFontSize
    )}px`,
  };
}
